import axios from 'axios';
import useAxios from 'axios-hooks';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { Box, Card, CircularProgress, DataGrid, Link, Select, Typography, ViewContainer } from 'components/AORedesign';
import { GET_NEWS, GET_NEWS_ARTICLES, NEWS_ARTICLES } from 'constants/api';
import featureFlags from '../../../../constants/featureFlags';
import { Feature } from '../../../Feature';
import NewsArticleModal from '../../../NewsArticles/NewsArticleModal';
import SearchNewsArticlesTypeRenderCell from './SearchNewsArticlesTypeRenderCell';

const menuItems = [{ label: 'Agents', value: 'Agents' }];

const SearchNewsArticles = ({ handleSetNewsArticleId }) => {
  const [newsGridRows, setNewsGridRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewArticle, setViewArticle] = useState(null);

  const [{ data: dataNewsReview, loading: loadingNewsReview, error: errorNewsReview }, executeNewsReview] = useAxios();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(GET_NEWS_ARTICLES);
        const allNews = data.map((article) => ({ id: article.seqId, ...article }));
        setNewsGridRows(allNews);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleOnTitleClick = async ({ row: { seqId } }) => {
    handleSetNewsArticleId(seqId);
  };

  const renderTitle = (params) => {
    const { title } = params.row;
    return <Link onClick={() => handleOnTitleClick(params)}>{title}</Link>;
  };

  const renderDisplayDates = (params) => {
    const { startDate, endDate } = params.row;
    const formattedStartDate = moment(startDate, 'MM/DD/YYYY').format('MM-DD-YYYY');
    const formattedEndDate = moment(endDate, 'MM/DD/YYYY').format('MM-DD-YYYY');
    const displayStartDate = `Start: ${formattedStartDate}`;
    const displayEndDate = `Stop: ${formattedEndDate}`;
    return (
      <Box>
        <Typography>{displayStartDate}</Typography>
        <Typography>{displayEndDate}</Typography>
      </Box>
    );
  };

  const renderDisplayStatus = (params) => {
    const { status } = params.row;
    if (status === 'D') {
      return <Typography>Display</Typography>;
    } else if (status === 'H') {
      return <Typography>Hide</Typography>;
    } else if (status === 'A') {
      return <Typography>Archive</Typography>;
    }
  };

  const handleOpenForNewsReview = (params) => {
    const { seqId, extension } = params.row;
    const trimmedExt = extension.trim(); // remove whitespace
    if (trimmedExt.length === 0) {
      setViewArticle(seqId);
      executeNewsReview({
        url: `${GET_NEWS}${NEWS_ARTICLES}/${seqId}`,
      });
    }
  };

  const handleClose = () => {
    setViewArticle(null);
  };

  const columns = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      renderCell: renderTitle,
    },
    {
      field: 'displayDates',
      headerName: 'Display Dates',
      width: 200,
      renderCell: renderDisplayDates,
    },
    {
      field: 'status',
      headerName: 'status',
      width: 150,
      renderCell: renderDisplayStatus,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 150,
      renderCell: (params) => (
        <SearchNewsArticlesTypeRenderCell
          type={params.row.type}
          extension={params.row.extension}
          seqId={params.row.seqId}
          handleOpenForNewsReview={() => handleOpenForNewsReview(params)}
        />
      ),
    },
  ];

  return (
    <Feature allowed={featureFlags.ACCESS_CONTENT_MGMT_ADMIN}>
      {loading ? (
        <Box mt={8}>
          <CircularProgress>Fetching news...</CircularProgress>
        </Box>
      ) : (
        <>
          <ViewContainer title="List News Articles" hideStartQuote hidePolicyViewChange>
            <Card>
              <>
                <Box mb={2}>
                  <Select
                    id="news-category-id"
                    labelId="news-category-id-label"
                    label="News Category"
                    name="newsCategory"
                    value="Agents"
                    disabled
                    menuItems={menuItems}
                  />
                </Box>
                <Box mb={4}>
                  <Typography>{newsGridRows?.length} records found on Agents news lookup</Typography>
                  <Box ml={2}>
                    <Typography>
                      click on the <b>Title</b> to modify the article attribute
                    </Typography>
                    <Typography>
                      click on the <b>Type</b> to preview the article contents
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ minWidth: '800px' }}>
                  <DataGrid columns={columns} rows={newsGridRows} noResultsMessage="No Results" />
                </Box>
              </>
            </Card>
          </ViewContainer>
          {viewArticle && (
            <NewsArticleModal open={viewArticle} handleClose={handleClose} loading={loadingNewsReview} error={errorNewsReview} data={dataNewsReview} />
          )}
        </>
      )}
    </Feature>
  );
};

SearchNewsArticles.propTypes = {
  handleSetNewsArticleId: PropTypes.func,
};

export default SearchNewsArticles;
